import { useState } from 'react';
import { mapi } from './api';
import { fmtDate, raha } from '../lib/format';
import {
  Button, ButtonRow, Chip, Field, Grid2, Hint, Muted, NeutralChip, Warn,
} from '../ui/primitives';
import type { AiStatus, CustomerRow } from './types';
import { Osio } from './Osio';

/* AI-avain ja malli ovat vain masterin hallinnassa: AI-kulut ovat laskutuksen
   perusta, joten asiakas ei voi vaihtaa avainta tai kalliimpaa mallia itse.
   Avain kirjoitetaan asiakkaan omaan kantaan (siellä sitä käyttävä prosessi
   on), mutta sitä ei lueta sieltä koskaan takaisin — näkyviin tulee vain
   tieto siitä, onko avain tallennettu. */

interface Props {
  asiakas: CustomerRow;
  onToast: (v: string) => void;
  onSaved: (ai: AiStatus) => void;
}

export function AiCard({ asiakas, onToast, onSaved }: Props) {
  const ai = asiakas.ai;
  const [muokkaa, setMuokkaa] = useState(false);
  const [avain, setAvain] = useState('');
  const [malli, setMalli] = useState(ai.model);

  const tallenna = async (clear?: boolean) => {
    try {
      const r = await mapi<{ ai: AiStatus }>(`/api/master/customers/${asiakas.id}/ai`, 'PUT',
        clear ? { clear: true } : { key: avain.trim() || undefined, model: malli.trim() });
      onSaved(r.ai);
      setAvain('');
      setMuokkaa(false);
      onToast(clear ? 'API-avain poistettu — AI-toiminnot lakkasivat toimimasta'
        : 'AI-asetukset tallennettu asiakkaan ympäristöön');
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
  };

  if (!ai.saatavilla) return null;

  return (
    <Osio>
      <h4>
        AI-avain ja malli
        {ai.configured
          ? <Chip $bg="#5C6B54">Avain tallennettu</Chip>
          : <NeutralChip>Ei avainta</NeutralChip>}
      </h4>

      {!muokkaa ? (
        <>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <span style={{ fontSize: 13.5 }}>
              Malli: <strong>{ai.model}</strong>
            </span>
            <Muted style={{ fontSize: 13 }}>
              Kuukauden käyttö: {asiakas.billing.calls} toimintoa ·{' '}
              {raha(asiakas.billing.cost, asiakas.billing.currency)} kulua
              {asiakas.billing.ai_free && (
                <>
                  {' '}· {raha(asiakas.billing.usage_price, asiakas.billing.currency)} kulutettu,{' '}
                  {raha(asiakas.billing.unbilled, asiakas.billing.currency)} ei laskutettu
                  {asiakas.ai_free_until ? ` (${fmtDate(asiakas.ai_free_until)} asti)` : ''}
                </>
              )}
              {' '}· {raha(asiakas.billing.amount, asiakas.billing.currency)} laskutetaan
            </Muted>
          </div>
          <ButtonRow style={{ margin: '6px 0 0' }}>
            <Button $small onClick={() => { setMalli(ai.model); setMuokkaa(true); }}>
              {ai.configured ? 'Vaihda avain tai malli' : 'Aseta API-avain'}
            </Button>
            {ai.configured && (
              <Button $small $variant="danger" onClick={() => {
                if (!confirm(`Poistetaanko ${asiakas.name} -asiakkaan API-avain?\n\n`
                  + 'AI-toiminnot lakkaavat toimimasta heti. Liidit ja viestit säilyvät.')) return;
                void tallenna(true);
              }}>
                Poista avain
              </Button>
            )}
            <Muted style={{ fontSize: 12.5 }}>
              Asiakas ei näe eikä voi vaihtaa avainta omassa sovelluksessaan.
            </Muted>
          </ButtonRow>
        </>
      ) : (
        <>
          <Hint style={{ marginTop: 0 }}>
            Avain kirjoitetaan asiakkaan ympäristöön eikä sitä lueta sieltä takaisin. Jos et
            muista nykyistä avainta, aseta uusi — vanha korvautuu.
          </Hint>
          <Grid2>
            <Field>
              <label htmlFor={`ai_key_${asiakas.id}`}>Anthropic API-avain</label>
              <input
                id={`ai_key_${asiakas.id}`} type="password" autoComplete="off"
                value={avain} onChange={e => setAvain(e.target.value)}
                placeholder={ai.configured ? 'Jätä tyhjäksi säilyttääksesi nykyisen' : 'sk-ant-…'}
              />
            </Field>
            <Field>
              <label htmlFor={`ai_model_${asiakas.id}`}>Malli</label>
              <input
                id={`ai_model_${asiakas.id}`}
                value={malli} onChange={e => setMalli(e.target.value)}
              />
            </Field>
          </Grid2>
          {!ai.configured && !avain.trim() && (
            <Warn>Ilman avainta AI-toiminnot eivät toimi asiakkaalla.</Warn>
          )}
          <ButtonRow>
            <Button $variant="primary" onClick={() => tallenna()}>Tallenna</Button>
            <Button onClick={() => { setMuokkaa(false); setAvain(''); }}>Peruuta</Button>
          </ButtonRow>
        </>
      )}
    </Osio>
  );
}
